/**
 * TD Ala Modal Web Component
 * 
 * A draggable modal that slides up from the bottom of the page
 * Features:
 * - Slides up animation from bottom
 * - Drag to close functionality (configurable threshold as % of viewport height)
 * - Backdrop click to close
 * - Close button support
 * - Escape key to close
 * - Scroll lock when open
 * - Dynamic content fetching with Shopify Section Rendering API
 * - Loading state with spinner
 * - Error handling and retry functionality
 * 
 * Configuration:
 * - data-drag-threshold: Percentage of viewport height to drag before closing (default: 15)
 * - data-cache-content: Whether to cache fetched content (default: true)
 * - data-show-loading: Whether to show loading spinner (default: true)
 * 
 * Usage:
 * - Section URL is provided dynamically via event data
 * - publish('modal-id:open', { sectionUrl: '/path?section_id=content' })
 * - publish('modal-id:refresh', { sectionUrl: '/path?section_id=content' })
 */
if (!customElements.get('td-ala-modal')) {
class TdAlaModal extends HTMLElement {
  
  /**
   * Initialize the component when connected to DOM
   */
  connectedCallback() {
    
    setTimeout(() => {
      this.initializeInstanceVariables();
      this.setupEventListeners();
    }, 5000);
  }

  /**
   * Initialize instance variables
   */
  initializeInstanceVariables() {
    // Modal state
    this.isOpen = false;
    this.isClosing = false;
    this.isDragging = false;
    this.startY = 0;
    this.currentY = 0;
    
    // Content fetching state
    this.isLoading = false;
    this.hasError = false;
    this.cachedContent = null;
    this.lastFetchUrl = null;
    this.currentSectionUrl = null;
    
    // Calculate drag threshold as percentage of viewport height
    const dragThresholdPercent = parseFloat(this.dataset.dragThreshold) || 50; // default 15% of viewport
    this.dragThreshold = (window.innerHeight * dragThresholdPercent) / 100;
    
    // Find existing DOM elements
    this.backdrop = this.querySelector('[data-backdrop]');
    this.modalContainer = this.querySelector('[data-container]');
    this.modalContent = this.querySelector('[data-content]');
    this.dragHandle = this.querySelector('[data-drag-handle]');
    this.closeButton = this.querySelector('[data-close-button]');
    this.isFullScreen = this.dataset.isFullScreen !== 'false';
    
    // Find template fragments
    this.loadingTemplate = this.querySelector('[data-template="loading"]');
    this.errorTemplate = this.querySelector('[data-template="error"]');
    
    // Get configuration from attributes
    this.modalId = this.dataset.modalId || 'td-modal';
    this.closeOnBackdrop = this.dataset.closeOnBackdrop !== 'false';
    this.closeOnEscape = this.dataset.closeOnEscape !== 'false';
    this.showDragHandle = this.dataset.showDragHandle !== 'false';
    this.showCloseButton = this.dataset.showCloseButton !== 'false';
    
    // Dynamic content configuration
    this.cacheContent = this.dataset.cacheContent !== 'false';
    this.showLoading = this.dataset.showLoading !== 'false';
    this.retryAttempts = parseInt(this.dataset.retryAttempts) || 3;
    
    // Store initial content as fallback
    this.staticContent = this.modalContent ? this.modalContent.innerHTML : '';
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    this.setupModalEventListeners();
    this.setupGlobalEventListeners();
    this.setupModalInteractionListeners();
  }

  /**
   * Setup modal-specific event listeners
   */
  setupModalEventListeners() {
    // Listen for open/close events
    this.openSubscriber = subscribe(`${this.modalId}:open`, this.handleOpen.bind(this));
    this.closeSubscriber = subscribe(`${this.modalId}:close`, this.handleClose.bind(this));
    
    // Listen for content refresh events
    this.refreshSubscriber = subscribe(`${this.modalId}:refresh`, this.handleRefreshContent.bind(this));
  }

  /**
   * Setup global event listeners
   */
  setupGlobalEventListeners() {
    // Escape key listener
    if (this.closeOnEscape) {
      this.escapeListener = this.handleEscapeKey.bind(this);
      document.addEventListener('keydown', this.escapeListener);
    }
    
    // Window resize listener to adjust modal height
    this.resizeListener = this.handleWindowResize.bind(this);
    window.addEventListener('resize', this.resizeListener);


  }

  /**
   * Setup interaction listeners after render
   */
  setupModalInteractionListeners() {
    // Backdrop click to close
    if (this.closeOnBackdrop && this.backdrop) {
      this.backdrop.addEventListener('click', this.handleBackdropClick.bind(this));
    }

    // Close button click to close
    if (this.showCloseButton && this.closeButton) {
      this.closeButton.addEventListener('click', this.handleCloseButtonClick.bind(this));
    }

    // Prevent clicks inside modal from closing
    if (this.modalContainer) {
      this.modalContainer.addEventListener('click', this.handleModalClick.bind(this));
    }

    // Drag functionality
    this.setupDragListeners();
  }

  /**
   * Setup drag event listeners
   */
  setupDragListeners() {
    const dragElement = this.dragHandle || this.modalContainer;
    
    if (dragElement) {
      // Mouse events
      // dragElement.addEventListener('mousedown', this.handleDragStart.bind(this));
      // document.addEventListener('mousemove', this.handleDragMove.bind(this));
      // document.addEventListener('mouseup', this.handleDragEnd.bind(this));
      
      // // Touch events
      // dragElement.addEventListener('touchstart', this.handleDragStart.bind(this), { passive: false });
      // document.addEventListener('touchmove', this.handleDragMove.bind(this), { passive: false });
      // document.addEventListener('touchend', this.handleDragEnd.bind(this));
    }
  }

  /**
   * Handle modal open - fetch content if needed
   * @param {Object} eventData - Event data containing sectionUrl and other options
   */
  async handleOpen(eventData = {}) {
    if (this.isOpen || this.isClosing) return;
    
    this.isOpen = true;
    this.isClosing = false;
    this.classList.remove('td-ala-modal--closing');
    this.classList.add('td-ala-modal--open');
    this.lockBodyScroll();

    // Set the section URL from event data
    if (eventData.sectionUrl) {
      this.currentSectionUrl = eventData.sectionUrl;
    }
    // Fetch content if URL is provided
    if (this.currentSectionUrl) {
      await this.fetchAndUpdateContent();
    } else {
      // If no new content to fetch, adjust height for existing static content
      requestAnimationFrame(() => {
        this.adjustModalHeight();
      });
    }

    publish(`${this.modalId}:opened`, { element: this });
  }

  /**
   * Fetch content from Shopify Section Rendering API
   * @param {number} attemptCount - Current retry attempt
   * @returns {Promise<boolean>} - Success status
   */
  async fetchAndUpdateContent(attemptCount = 0) {
    if (!this.currentSectionUrl) {
      console.warn('TD Ala Modal: No section URL provided for content fetching');
      return false;
    }

    // Check if we have cached content and should use it
    if (this.cacheContent && this.cachedContent && this.lastFetchUrl === this.currentSectionUrl) {
      this.updateModalContent(this.cachedContent);
      return true;
    }

    try {
      this.setLoadingState(true);
      this.hasError = false;

      const response = await fetch(this.currentSectionUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const htmlContent = await response.text();
      
      // Cache the content if caching is enabled
      if (this.cacheContent) {
        this.cachedContent = htmlContent;
        this.lastFetchUrl = this.currentSectionUrl;
      }

      this.updateModalContent(htmlContent);
      this.setLoadingState(false);
      
      publish(`${this.modalId}:contentLoaded`, { element: this, content: htmlContent });
      
      return true;
    } catch (error) {
      console.error(`TD Ala Modal: Failed to fetch content from ${this.currentSectionUrl}:`, error);
      
      // Retry logic
      if (attemptCount < this.retryAttempts) {
        console.log(`TD Ala Modal: Retrying... (${attemptCount + 1}/${this.retryAttempts})`);
        
        // Exponential backoff: wait 1s, then 2s, then 4s
        const delay = Math.pow(2, attemptCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        
        return await this.fetchAndUpdateContent(attemptCount + 1);
      }
      
      // All retries failed
      this.handleFetchError(error);
      return false;
    }
  }

  /**
   * Handle fetch errors by showing error state
   * @param {Error} error - The error that occurred
   */
  handleFetchError(error) {
    this.hasError = true;
    this.setLoadingState(false);
    
    const errorContent = this.getErrorTemplate(error.message);
    this.updateModalContent(errorContent);
    
    publish(`${this.modalId}:contentError`, { element: this, error });
  }

  /**
   * Get error template with populated error message
   * @param {string} errorMessage - The error message to display
   * @returns {string} - HTML content for error state
   */
  getErrorTemplate(errorMessage) {
    if (!this.errorTemplate) {
      // Fallback if template is missing
      return `
        <div class="td-ala-modal__error">
          <div class="td-ala-modal__error-icon">⚠️</div>
          <h3 class="td-ala-modal__error-title">Failed to Load Content</h3>
          <p class="td-ala-modal__error-message">${errorMessage}</p>
          <button type="button" class="td-ala-modal__retry-btn" data-retry-action>
            Try Again
          </button>
        </div>
      `;
    }

    // Clone the template
    const errorClone = this.errorTemplate.cloneNode(true);
    
    // Update the error message
    const errorMessageElement = errorClone.querySelector('[data-error-message]');
    if (errorMessageElement) {
      errorMessageElement.textContent = errorMessage;
    }
    
    return errorClone.outerHTML;
  }

  /**
   * Get loading template
   * @returns {string} - HTML content for loading state
   */
  getLoadingTemplate() {
    if (!this.loadingTemplate) {
      // Fallback if template is missing
      return `
        <div class="td-ala-modal__loading">
          <div class="td-ala-modal__spinner">
            <svg class="td-ala-modal__spinner-icon" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-dasharray="32" stroke-dashoffset="32">
                <animate attributeName="stroke-dashoffset" dur="1s" values="32;0" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
          <p class="td-ala-modal__loading-text">Loading content...</p>
        </div>
      `;
    }

    return this.loadingTemplate.outerHTML;
  }

  /**
   * Set loading state
   * @param {boolean} isLoading - Whether content is loading
   */
  setLoadingState(isLoading) {
    this.isLoading = isLoading;
    
    if (isLoading) {
      this.classList.add('td-ala-modal--loading');
      if (this.showLoading) {
        this.updateModalContent(this.getLoadingTemplate());
      }
    } else {
      this.classList.remove('td-ala-modal--loading');
    }
  }

  /**
   * Update modal content
   * @param {string} htmlContent - New HTML content
   */
  updateModalContent(htmlContent) {
    if (this.modalContent) {
      this.modalContent.innerHTML = htmlContent;
      
      // Setup retry button event listener if it exists
      const retryButton = this.modalContent.querySelector('[data-retry-button], [data-retry-action]');
      if (retryButton) {
        retryButton.addEventListener('click', this.handleRetry.bind(this));
      }
      
      // Adjust modal height to fit content after a short delay to ensure DOM is updated
      requestAnimationFrame(() => {
        this.adjustModalHeight();
      });
      
      // Dispatch event for any scripts that need to reinitialize
      publish(`${this.modalId}:contentUpdated`, { element: this });
    }
  }

  /**
   * Adjust modal height to fit content
   */
  adjustModalHeight() {
    if (!this.modalContainer || !this.modalContent || this.isFullScreen) return;
    
    // Add transition class for smooth height animation (unless currently dragging)
    if (!this.isDragging) {
      this.classList.add('td-ala-modal--resizing');
      this.modalContainer.style.transition = 'height 0.3s ease, max-height 0.3s ease';
    }
    
    // Get the natural height of the content
    const contentHeight = this.modalContent.scrollHeight;
    
    // Calculate maximum available height (viewport height minus some padding)
    const viewportHeight = window.innerHeight;
    const maxHeight = Math.min(viewportHeight * 0.9, viewportHeight - 40); // 90% of viewport or viewport - 40px padding
    
    // Determine the target height
    const targetHeight = Math.min(contentHeight, maxHeight);
    
    // Apply the height to the modal container
    this.modalContainer.style.height = `${targetHeight}px`;
    this.modalContainer.style.maxHeight = `${maxHeight}px`;
    
    // If content is taller than max height, ensure content area is scrollable
    if (contentHeight > maxHeight) {
      this.modalContent.style.overflowY = 'auto';
      this.modalContent.style.height = '100%';
    } else {
      this.modalContent.style.overflowY = 'visible';
      this.modalContent.style.height = 'auto';
    }
    
    // Remove transition class after animation completes
    if (!this.isDragging) {
      setTimeout(() => {
        this.classList.remove('td-ala-modal--resizing');
        this.modalContainer.style.transition = '';
      }, 300); // Match transition duration
    }
    
    // Publish event for height adjustment
    publish(`${this.modalId}:heightAdjusted`, { 
      element: this, 
      contentHeight,
      targetHeight,
      maxHeight 
    });
  }

  /**
   * Handle retry button click
   */
  async handleRetry() {
    if (this.currentSectionUrl && !this.isLoading) {
      // Clear cached content to force refetch
      this.cachedContent = null;
      this.lastFetchUrl = null;
      await this.fetchAndUpdateContent();
    }
  }

  /**
   * Handle refresh content event - opens modal if not open and fetches content
   * @param {Object} eventData - Event data containing sectionUrl and other options
   */
  async handleRefreshContent(eventData = {}) {
    // Update section URL if provided
    if (eventData.sectionUrl) {
      this.currentSectionUrl = eventData.sectionUrl;
    }
    
    // Clear cached content to force refetch
    this.cachedContent = null;
    this.lastFetchUrl = null;
    
    // If modal is not open, open it first
    if (!this.isOpen && !this.isClosing) {
      await this.handleOpen(eventData);
    } else if (this.currentSectionUrl) {
      // Modal is already open, just refresh the content
      await this.fetchAndUpdateContent();
    }
  }

  /**
   * Handle modal close with smooth slide-out animation
   */
  handleClose() {
    if (!this.isOpen || this.isClosing) return;
    
    this.isClosing = true;
    this.classList.add('td-ala-modal--closing');
    
    // Wait for slide-out animation to complete before fully closing
    setTimeout(() => {
      this.isOpen = false;
      this.isClosing = false;
      this.classList.remove('td-ala-modal--open', 'td-ala-modal--closing', 'td-ala-modal--loading');
      this.unlockBodyScroll();
      
      // Reset content to static content and clear section URL
      if (this.currentSectionUrl && this.staticContent) {
        this.updateModalContent(this.staticContent);
      }
      this.currentSectionUrl = null;
      
      publish(`${this.modalId}:closed`, { element: this });
    }, 300); // Match CSS transition duration
  }

  /**
   * Handle escape key press
   * @param {KeyboardEvent} event 
   */
  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.isOpen && !this.isClosing) {
      this.handleClose();
    }
  }

  /**
   * Handle backdrop click
   * @param {Event} event 
   */
  handleBackdropClick(event) {
    if (event.target === this.backdrop && !this.isClosing) {
      this.handleClose();
    }
  }

  /**
   * Handle close button click
   * @param {Event} event 
   */
  handleCloseButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isClosing) {
      this.handleClose();
    }
  }

  /**
   * Handle clicks inside modal (prevent closing)
   * @param {Event} event 
   */
  handleModalClick(event) {
    event.stopPropagation();
  }

  /**
   * Handle drag start
   * @param {MouseEvent|TouchEvent} event 
   */
  handleDragStart(event) {
    if (this.isClosing) return;
    
    this.isDragging = true;
    this.startY = this.getClientY(event);
    
    // Add dragging class to disable CSS transitions and animations
    this.classList.add('td-ala-modal--dragging');
    
    event.preventDefault();
  }

  /**
   * Handle drag move
   * @param {MouseEvent|TouchEvent} event 
   */
  handleDragMove(event) {
    if (!this.isDragging || this.isClosing) return;
    
    event.preventDefault();
    this.currentY = this.getClientY(event);
    const deltaY = this.currentY - this.startY;
    
    // Only allow dragging down
    if (deltaY > 0) {
      this.modalContainer.style.transform = `translateY(${deltaY}px)`;
      
      // Add opacity fade as user drags
      const opacity = Math.max(0.3, 1 - (deltaY / 200));
      this.backdrop.style.backgroundColor = `rgba(0, 0, 0, ${opacity * 0.5})`;
    }
  }

  /**
   * Handle drag end
   * @param {MouseEvent|TouchEvent} event 
   */
  handleDragEnd(event) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    
    // Remove dragging class to re-enable CSS transitions and animations
    this.classList.remove('td-ala-modal--dragging');
    this.modalContainer.style.transform = '';
    this.backdrop.style.backgroundColor = '';
    
    const deltaY = this.currentY - this.startY;
    
    // Close if dragged beyond threshold
    if (deltaY > this.dragThreshold && !this.isClosing) {
      this.handleClose();
    }
    // If not closing, the CSS will handle snapping back to original position
  }

  /**
   * Get clientY from mouse or touch event
   * @param {MouseEvent|TouchEvent} event 
   * @returns {number}
   */
  getClientY(event) {
    return event.touches ? event.touches[0].clientY : event.clientY;
  }

  /**
   * Lock body scroll when modal is open
   */
  lockBodyScroll() {
    document.body.style.overflow = 'hidden';
    document.body.classList.add('td-ala-modal-open');
  }

  /**
   * Unlock body scroll when modal is closed
   */
  unlockBodyScroll() {
    document.body.style.overflow = '';
    document.body.classList.remove('td-ala-modal-open');
  }

  /**
   * Handle window resize to adjust modal height
   */
  handleWindowResize() {
    // Debounce resize adjustments to prevent excessive calls
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    this.resizeTimeout = setTimeout(() => {
      if (this.isOpen && !this.isClosing) {
        this.adjustModalHeight();
      }
    }, 150); // Wait 150ms after resize stops
  }

  /**
   * Cleanup when component is disconnected
   */
  disconnectedCallback() {
    // Clean up subscribers
    if (this.openSubscriber) {
      this.openSubscriber();
    }
    if (this.closeSubscriber) {
      this.closeSubscriber();
    }
    if (this.refreshSubscriber) {
      this.refreshSubscriber();
    }
    
    // Clean up global listeners
    if (this.escapeListener) {
      document.removeEventListener('keydown', this.escapeListener);
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
    
    // Clean up timers
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Unlock body scroll if modal was open
    if (this.isOpen) {
      this.unlockBodyScroll();
    }
  }
  
  /**
   * Public method to open modal
   */
  open() {
    this.handleOpen();
  }
  
  /**
   * Public method to close modal
   */
  close() {
    this.handleClose();
  }
}

// Register the custom element only if it hasn't been defined yet
  customElements.define('td-ala-modal', TdAlaModal);
} 